/**
 * App.tsx - Main Application Component
 * Routes between screens based on game phase
 */

import { useGame } from './context/GameContext';
import { CartridgeSelectScreen } from './screens/CartridgeSelectScreen';
import { BootScreen } from './screens/BootScreen';
import { LoadingScreen } from './screens/LoadingScreen';
import { SpinScreen } from './screens/SpinScreen';
import { PresentationScreen } from './screens/PresentationScreen';
import { RecapScreen } from './screens/RecapScreen';
import { OrphanCheckScreen } from './screens/OrphanCheckScreen';
import { GameModeSelectScreen } from './screens/GameModeSelectScreen';
import { PacmanMazeScreen } from './screens/PacmanMazeScreen';
import { PacmanKanbanScreen } from './screens/PacmanKanbanScreen';
import { useKonamiCode } from './hooks/useKonamiCode';
import { preloadAllSounds } from './hooks/useAudio';
import { SoundIcon } from './components/SoundIcon';
import { useEffect } from 'react';
import './App.css';

export default function App() {
  const { state, actions } = useGame();

  // Listen for Konami code to toggle Chouquette mode
  useKonamiCode(actions.toggleChouquetteMode);

  // Preload sounds on mount
  useEffect(() => {
    preloadAllSounds();
  }, []);

  // Phases où le bouton son est visible (phases de jeu actif)
  const showSoundToggle = ['SPIN', 'SPIN_RESULT', 'PACMAN_MAZE', 'PACMAN_KANBAN'].includes(state.phase);

  // Render the current screen based on game phase
  const renderScreen = () => {
    switch (state.phase) {
      case 'CARTRIDGE_SELECT':
        return <CartridgeSelectScreen />;
      case 'BOOT':
        return <BootScreen />;
      case 'LOADING':
        return <LoadingScreen />;
      case 'ORPHAN_CHECK':
        return <OrphanCheckScreen />;
      case 'GAME_MODE_SELECT':
        return <GameModeSelectScreen />;
      case 'SPIN':
      case 'SPIN_RESULT':
        return <SpinScreen />;
      case 'PACMAN_MAZE':
        return <PacmanMazeScreen />;
      case 'PACMAN_KANBAN':
        return <PacmanKanbanScreen />;
      case 'PRESENTATION':
        return <PresentationScreen />;
      case 'RECAP':
        return <RecapScreen />;
      default:
        return <BootScreen />;
    }
  };

  return (
    <div className="app">
      {/* Chouquette mode indicator */}
      {state.chouquetteMode && (
        <div className="chouquette-indicator">
          CHOUQUETTE MODE
        </div>
      )}

      {/* Error display */}
      {state.error && (
        <div className="error-banner">
          {state.error}
          <button onClick={() => actions.setError(null)}>✕</button>
        </div>
      )}

      {/* Sound toggle button */}
      {showSoundToggle && (
        <button
          className="sound-toggle"
          onClick={actions.toggleSound}
          title={state.soundEnabled ? 'Couper le son' : 'Activer le son'}
        >
          <SoundIcon enabled={state.soundEnabled} size={28} />
        </button>
      )}

      {/* Main screen */}
      {renderScreen()}
    </div>
  );
}
